import { useMemo, useState } from "react";
import { BarChart3, ArrowRight, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import { demoFinancialChartsData } from "@/lib/landing-demo-data";

// Chart configuration
interface ChartConfig {
  key: keyof typeof demoFinancialChartsData.charts;
  title: string;
  metric: string;
  type: "bar" | "line";
  color: string;
  unit: "currency" | "percentage" | "ratio" | "currency_small";
}

const CHARTS: ChartConfig[] = [
  {
    key: "revenue",
    title: "Revenue",
    metric: "revenue",
    type: "bar",
    color: "#3b82f6",
    unit: "currency",
  },
  {
    key: "net_income",
    title: "Net Income",
    metric: "net_income",
    type: "bar",
    color: "#10b981",
    unit: "currency",
  },
  {
    key: "gross_margin",
    title: "Gross Margin",
    metric: "gross_margin",
    type: "line",
    color: "#8b5cf6",
    unit: "percentage",
  },
  {
    key: "free_cash_flow",
    title: "Free Cash Flow",
    metric: "free_cash_flow",
    type: "bar",
    color: "#06b6d4",
    unit: "currency",
  },
  {
    key: "eps",
    title: "EPS",
    metric: "eps",
    type: "bar",
    color: "#f59e0b",
    unit: "currency_small",
  },
  {
    key: "price_to_earnings",
    title: "P/E Ratio",
    metric: "price_to_earnings",
    type: "line",
    color: "#ec4899",
    unit: "ratio",
  },
];

function formatValue(value: number, unit: ChartConfig["unit"]): string {
  switch (unit) {
    case "currency":
      if (Math.abs(value) >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
      if (Math.abs(value) >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
      if (Math.abs(value) >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
      return `$${value.toLocaleString()}`;
    case "currency_small":
      return `$${value.toFixed(2)}`;
    case "percentage":
      return `${value.toFixed(1)}%`;
    case "ratio":
      return `${value.toFixed(1)}x`;
    default:
      return value.toString();
  }
}

function calculateCAGR(data: Record<string, unknown>[], metric: string): number | null {
  if (data.length < 2) return null;
  const first = data[0]?.[metric];
  const last = data[data.length - 1]?.[metric];
  if (typeof first !== "number" || typeof last !== "number" || first <= 0 || last <= 0) return null;
  const years = data.length - 1;
  return (Math.pow(last / first, 1 / years) - 1) * 100;
}

function resolveLogoUrl(url?: string): string | null {
  if (!url) return null;
  if (!url.includes("img.logo.dev")) return url;
  try {
    const parsed = new URL(url);
    const token = import.meta.env?.VITE_LOGO_DEV_TOKEN as string | undefined;
    if (token && !parsed.searchParams.has("token")) {
      parsed.searchParams.set("token", token);
    }
    return parsed.toString();
  } catch {
    return url;
  }
}

function MiniChartCard({ config }: { config: ChartConfig }) {
  const data = demoFinancialChartsData.charts[config.key];
  const lastPoint = data[data.length - 1];
  const lastValue = lastPoint?.[config.metric as keyof typeof lastPoint] as unknown as number;

  const cagr = useMemo(() => calculateCAGR(data, config.metric), [data, config.metric]);

  const ChartComponent = config.type === "line" ? LineChart : BarChart;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="py-2.5 px-3 flex flex-row items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <CardTitle className="text-xs font-medium truncate">
            {config.title}
          </CardTitle>
          {lastValue !== undefined && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 shrink-0">
              {formatValue(lastValue, config.unit)}
            </Badge>
          )}
          {cagr !== null && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 shrink-0 hidden sm:inline-flex">
              CAGR: {cagr >= 0 ? "+" : ""}{cagr.toFixed(1)}%
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-2 pt-0">
        <ResponsiveContainer width="100%" height={120}>
          <ChartComponent
            data={data}
            margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} />
            <XAxis
              dataKey="periodKey"
              tick={{ fontSize: 9 }}
              tickLine={false}
              axisLine={false}
              interval={1}
            />
            <RechartsTooltip
              content={({ active, payload, label }) => {
                if (!active || !payload || payload.length === 0) return null;
                const value = payload[0]?.value;
                const formatted =
                  typeof value === "number" && Number.isFinite(value)
                    ? formatValue(value, config.unit)
                    : "-";
                return (
                  <div className="bg-card border border-border rounded-md px-2 py-1.5 text-[11px] shadow-sm">
                    <div className="text-muted-foreground mb-1">{label}</div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-foreground">{config.title}</span>
                      <span className="font-mono">{formatted}</span>
                    </div>
                  </div>
                );
              }}
            />
            {config.type === "bar" ? (
              <Bar
                dataKey={config.metric}
                fill={config.color}
                radius={[2, 2, 0, 0]}
              />
            ) : (
              <Line
                type="monotone"
                dataKey={config.metric}
                stroke={config.color}
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            )}
          </ChartComponent>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

export default function FinancialsShowcase() {
  const [logoError, setLogoError] = useState(false);
  const logoUrl = useMemo(
    () => resolveLogoUrl(demoFinancialChartsData.company.logo),
    [],
  );

  return (
    <section className="w-full py-20 bg-muted/30">
      <div className="container px-4 md:px-6 mx-auto">
        {/* Section Header */}
        <div className="flex flex-col items-center text-center space-y-4 mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-widest">
            <BarChart3 className="w-3 h-3" />
            Financial Analysis
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-serif">
            Analyze Your Stocks With{" "}
            <span className="text-primary">Deep Fundamentals</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl">
            Dive into 5+ years of revenue, margins, cash flow and valuation data 
            for any stock. Make informed decisions backed by fundamentals, not gut feelings.
          </p>
        </div>

        {/* Financial Charts Card */}
        <div className="max-w-5xl mx-auto">
          <div className="rounded-2xl border border-border/50 bg-card shadow-xl overflow-hidden">
            {/* Company Header */}
            <div className="p-4 md:p-6 border-b border-border/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {logoUrl && !logoError ? (
                    <img
                      src={logoUrl}
                      alt={`${demoFinancialChartsData.company.name} logo`}
                      className="h-10 w-10 rounded-full border border-border/50 bg-background object-contain p-1 shrink-0"
                      loading="lazy"
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm border border-border/50 shrink-0">
                      {demoFinancialChartsData.company.symbol.slice(0, 2)}
                    </div>
                  )}
                  <div>
                    <div className="font-semibold text-sm flex items-center gap-2">
                      {demoFinancialChartsData.company.name}
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-normal">
                        {demoFinancialChartsData.company.symbol}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Annual Fundamentals
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="gap-1 text-xs">
                    <TrendingUp className="h-3 w-3" />
                    21 charts available
                  </Badge>
                </div>
              </div>
            </div>

            {/* Charts Grid */}
            <div className="p-4 md:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {CHARTS.map((chart) => (
                  <MiniChartCard key={chart.key} config={chart} />
                ))}
              </div>
            </div>

            {/* Footer CTA */}
            <div className="p-4 border-t border-border/50 bg-muted/20">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Income, cash flow, margins, balance sheet, valuation & more
                </p>
                <Button variant="outline" size="sm" className="gap-2" asChild>
                  <a href="/login">
                    Explore Financial Data
                    <ArrowRight className="h-3 w-3" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
